import {
  AppShell, Burger, Group, Text, Loader, Center, Button, Stack,
  NavLink, Menu, Avatar, UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  IconLayoutDashboard, IconListDetails, IconDeviceDesktop, IconUpload,
  IconDatabaseImport, IconLogout, IconFileSpreadsheet,
} from '@tabler/icons-react';
import { useState, type ComponentType } from 'react';
import { useLogin, useLogout, useMe } from '../api/auth';
import { t } from '../lib/strings';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  match: (path: string) => boolean;
  admin?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: t.nav.dashboard, icon: IconLayoutDashboard, match: (p) => p === '/' },
  { to: '/cases', label: t.nav.cases, icon: IconListDetails, match: (p) => p.startsWith('/cases') },
  { to: '/palvelutunnukset', label: t.nav.palvelutunnukset, icon: IconDeviceDesktop, match: (p) => p.startsWith('/palvelutunnukset') },
  { to: '/import', label: t.nav.import, icon: IconUpload, match: (p) => p.startsWith('/import') },
  { to: '/korttitoimitus', label: t.nav.korttitoimitus, icon: IconFileSpreadsheet, match: (p) => p.startsWith('/korttitoimitus') },
  { to: '/admin/migrate', label: t.nav.admin, icon: IconDatabaseImport, match: (p) => p.startsWith('/admin'), admin: true },
];

/** Keyboard-reachable skip link, revealed on focus (no global CSS needed). */
function SkipLink() {
  const [focused, setFocused] = useState(false);
  return (
    <a
      href="#main"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        position: 'absolute', left: 8, top: focused ? 8 : -48, zIndex: 2000,
        background: 'var(--mantine-color-blue-6)', color: '#fff',
        padding: '6px 12px', borderRadius: 6, fontSize: 14, transition: 'top 120ms ease',
      }}
    >
      {t.app.skip}
    </a>
  );
}

export function Root() {
  const [opened, { toggle, close }] = useDisclosure();
  const me = useMe();
  const login = useLogin();
  const logout = useLogout();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (me.isLoading) {
    return <Center h="100vh"><Loader /></Center>;
  }

  if (!me.data) {
    return (
      <Center h="100vh">
        <Stack gap="md" align="center">
          <Text size="xl" fw={700}>{t.app.title}</Text>
          <Text c="dimmed">{t.auth.notSignedIn}</Text>
          <Button onClick={login}>{t.auth.signIn}</Button>
        </Stack>
      </Center>
    );
  }

  const isAdmin = me.data.userRoles.includes('admin');
  const email = me.data.userDetails;
  const initials = email.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() || '?';

  return (
    <>
      <SkipLink />
      <AppShell
        header={{ height: 60 }}
        navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
        padding="md"
      >
        <AppShell.Header>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label={t.app.menu} />
              <Text fw={700} size="lg">{t.app.title}</Text>
              <Text c="dimmed" size="sm" visibleFrom="sm">{t.app.subtitle}</Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Menu position="bottom-end" withinPortal shadow="md" width={240}>
                <Menu.Target>
                  <UnstyledButton aria-label={email}>
                    <Group gap="xs" wrap="nowrap">
                      <Avatar size={30} radius="xl" color="blue">{initials}</Avatar>
                      <Text size="sm" visibleFrom="sm" maw={180} truncate>{email}</Text>
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{email}</Menu.Label>
                  <Menu.Item leftSection={<IconLogout size={16} />} onClick={logout}>
                    {t.nav.logout}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p="md">
          <Stack gap={4}>
            {NAV.filter((item) => !item.admin || isAdmin).map((item) => {
              const active = item.match(pathname);
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  component={Link}
                  to={item.to}
                  label={item.label}
                  leftSection={<Icon size={18} />}
                  active={active}
                  aria-current={active ? 'page' : undefined}
                  variant="light"
                  onClick={close}
                />
              );
            })}
          </Stack>
        </AppShell.Navbar>

        {/* tabIndex=-1 so the skip link actually moves keyboard focus here. */}
        <AppShell.Main id="main" tabIndex={-1}>
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </>
  );
}
